import { REQUEST, SUCCESS, FAILURE } from '@/plugins/store'

export default {
  // Upload creative mutations
  UPLOAD_CREATIVE_REQUEST(state) {
    REQUEST(state)
    state.uploadProgress = 0
  },

  UPLOAD_CREATIVE_PROGRESS(state, progress) {
    state.uploadProgress = progress
  },

  UPLOAD_CREATIVE_SUCCESS(state, creative) {
    SUCCESS(state)
    state.currentUpload = creative
    state.uploadedCreatives.unshift(creative)
    state.uploadProgress = 100
  },

  UPLOAD_CREATIVE_FAILURE(state, error) {
    FAILURE(state, error)
    state.uploadProgress = 0
    state.currentUpload = null
  },

  // Form data mutations
  SET_UPLOAD_FORM(state, formData) {
    state.uploadForm = { ...state.uploadForm, ...formData }
  },

  RESET_UPLOAD_FORM(state) {
    state.uploadForm = {
      type: 'video',
      uploadMethod: 'UPLOAD_BY_FILE',
      advertiserId: '7349406653265084417', // Keep default dev advertiser ID
      file: null,
      url: '',
      fileId: '',
      videoId: '',
      flawDetect: false,
      autoFixEnabled: false,
      autoBindEnabled: false,
      adText: '',
      landingPageUrl: '',
      locationCodes: ['US'], // Keep default location code
      isEcommerce: false,
    }
    state.uploadProgress = 0
    state.currentUpload = null
  },

  // File validation mutations
  SET_FILE_VALIDATION(state, validation) {
    state.fileValidation = validation
  },

  // Pre-review mutations
  SUBMIT_PRE_REVIEW_REQUEST(state) {
    REQUEST(state)
  },

  SUBMIT_PRE_REVIEW_SUCCESS(state, result) {
    SUCCESS(state)
    // Update the creative with pre-review status
    const creativeIndex = state.uploadedCreatives.findIndex(c => c.id === result.creative_id)
    if (creativeIndex !== -1) {
      state.uploadedCreatives[creativeIndex] = {
        ...state.uploadedCreatives[creativeIndex],
        preReviewStatus: 'submitted',
        preReviewData: result,
      }
    }
  },

  SUBMIT_PRE_REVIEW_FAILURE(state, error) {
    FAILURE(state, error)
  },

  // Creatives list mutations
  FETCH_CREATIVES_REQUEST(state) {
    REQUEST(state)
  },

  FETCH_CREATIVES_SUCCESS(state, { creatives, total, page }) {
    SUCCESS(state)
    state.creativesList = creatives
    state.creativesTotal = total
    state.creativesPage = page
  },

  FETCH_CREATIVES_FAILURE(state, error) {
    FAILURE(state, error)
  },

  // Creative status mutations
  UPDATE_CREATIVE_STATUS(state, { creativeId, status }) {
    const creativeIndex = state.creativesList.findIndex(c => c.id === creativeId)
    if (creativeIndex !== -1) {
      state.creativesList[creativeIndex].status = status
    }

    const uploadedIndex = state.uploadedCreatives.findIndex(c => c.id === creativeId)
    if (uploadedIndex !== -1) {
      state.uploadedCreatives[uploadedIndex].status = status
    }
  },

  // Delete creative mutations
  DELETE_CREATIVE_REQUEST(state) {
    REQUEST(state)
  },

  DELETE_CREATIVE_SUCCESS(state, creativeId) {
    SUCCESS(state)
    state.creativesList = state.creativesList.filter(c => c.id !== creativeId)
    state.uploadedCreatives = state.uploadedCreatives.filter(c => c.id !== creativeId)
  },

  DELETE_CREATIVE_FAILURE(state, error) {
    FAILURE(state, error)
  },

  // Filters mutations
  SET_CREATIVES_FILTERS(state, filters) {
    state.creativesFilters = { ...state.creativesFilters, ...filters }
  },

  SET_CREATIVES_PAGE(state, page) {
    state.creativesPage = page
  },

  SET_CREATIVES_PAGE_SIZE(state, pageSize) {
    state.creativesPageSize = pageSize
  },

  SET_CREATIVES_SORT(state, { sortBy, sortDesc }) {
    state.creativesSort = { sortBy, sortDesc }
  },

  // Bulk actions mutations
  BULK_DELETE_CREATIVES_REQUEST(state) {
    REQUEST(state)
  },

  BULK_DELETE_CREATIVES_SUCCESS(state, deletedIds) {
    SUCCESS(state)
    state.creativesList = state.creativesList.filter(c => !deletedIds.includes(c.id))
    state.uploadedCreatives = state.uploadedCreatives.filter(c => !deletedIds.includes(c.id))
    state.creativesTotal = Math.max(0, state.creativesTotal - deletedIds.length)
  },

  BULK_DELETE_CREATIVES_FAILURE(state, error) {
    FAILURE(state, error)
  },

  BULK_RESUBMIT_CREATIVES_REQUEST(state) {
    REQUEST(state)
  },

  BULK_RESUBMIT_CREATIVES_SUCCESS(state, results) {
    SUCCESS(state)
    // Update creative statuses based on resubmission results
    results.forEach(result => {
      const creativeIndex = state.creativesList.findIndex(c => c.id === result.creative_id)
      if (creativeIndex !== -1) {
        state.creativesList[creativeIndex] = {
          ...state.creativesList[creativeIndex],
          preReviewStatus: 'pending',
          preReviewData: result,
        }
      }
    })
  },

  BULK_RESUBMIT_CREATIVES_FAILURE(state, error) {
    FAILURE(state, error)
  },

  // Creative details mutations
  UPDATE_CREATIVE_PRE_REVIEW_STATUS(state, { creativeId, preReviewStatus, rejectReasons }) {
    const creativeIndex = state.creativesList.findIndex(c => c.id === creativeId)
    if (creativeIndex !== -1) {
      state.creativesList[creativeIndex] = {
        ...state.creativesList[creativeIndex],
        preReviewStatus,
        rejectReasons: rejectReasons || [],
      }
    }

    const uploadedIndex = state.uploadedCreatives.findIndex(c => c.id === creativeId)
    if (uploadedIndex !== -1) {
      state.uploadedCreatives[uploadedIndex] = {
        ...state.uploadedCreatives[uploadedIndex],
        preReviewStatus,
        rejectReasons: rejectReasons || [],
      }
    }
  },

  UPDATE_PRE_REVIEW_TASK_STATUS(state, { taskId, status, materials }) {
    if (state.preReviewTasks) {
      const taskIndex = state.preReviewTasks.findIndex(t => t.task_id === taskId)
      if (taskIndex !== -1) {
        state.preReviewTasks[taskIndex] = {
          ...state.preReviewTasks[taskIndex],
          status,
          materials,
        }
      }
    }
  },

  FETCH_PRE_REVIEW_HISTORY_REQUEST(state) {
    state.loading = true
    state.error = null
  },

  FETCH_PRE_REVIEW_HISTORY_SUCCESS(state, { tasks, pagination }) {
    state.loading = false
    state.error = null
    state.preReviewTasks = tasks
    state.preReviewPagination = pagination
  },

  FETCH_PRE_REVIEW_HISTORY_FAILURE(state, error) {
    state.loading = false
    state.error = error
  },
}
